import { useEffect, useState } from 'react';
import { Calendar, Clock, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const toDateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatHumanTime = (iso) =>
  new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

/**
 * Casa de Candy — availability-aware date + time picker.
 *
 * Renders 14 day chips + a list of free slot times for the picked day.
 * Hides anything the backend doesn't return — the customer literally cannot
 * select an impossible combination. Conflicts surface a friendly retry note.
 */
export function AvailabilitySlotPicker({ serviceCode = 'HR', value, onChange, conflictMessage }) {
  const [date, setDate] = useState(() => toDateKey(new Date()));
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Build the next 14 day chips (today + 13 ahead)
  const days = Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(
          `${API_URL}/api/availability?date=${date}&service_code=${encodeURIComponent(serviceCode)}`
        );
        if (!res.ok) throw new Error('availability_failed');
        const data = await res.json();
        if (!cancelled) setSlots(data.slots || []);
      } catch (_e) {
        if (!cancelled) setError('Could not load availability.');
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [date, serviceCode]);

  // If the parent's selected slot becomes unavailable (someone else booked it),
  // clear the selection so they can pick another.
  useEffect(() => {
    if (value && !slots.find((s) => s.start === value)) {
      onChange?.(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots]);

  return (
    <div className="space-y-3" data-testid="availability-picker">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A7A3BA]">
        <Calendar className="w-3.5 h-3.5" /> Pick a date
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="picker-days">
        {days.map((d) => {
          const key = toDateKey(d);
          const isToday = key === toDateKey(new Date());
          const active = key === date;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setDate(key)}
              className={`flex-shrink-0 w-16 px-2 py-2 rounded-xl border transition-all ${
                active
                  ? 'border-[#FF4DA6] bg-[#FF4DA6]/15 text-white shadow-[0_0_24px_-8px_rgba(255,77,166,0.6)]'
                  : 'border-white/10 bg-[#0B0A12] text-[#A7A3BA] hover:border-[#FFB6D8]/40 hover:text-[#F4F2FF]'
              }`}
              data-testid={`picker-day-${key}`}
            >
              <span className="block text-[10px] uppercase tracking-wider opacity-80">
                {d.toLocaleDateString([], { weekday: 'short' })}
              </span>
              <span className="block text-base font-semibold">{d.getDate()}</span>
              {isToday && <span className="block text-[9px] uppercase text-[#FFB6D8]">today</span>}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A7A3BA] pt-1">
        <Clock className="w-3.5 h-3.5" /> Available times
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[#A7A3BA] py-3">
          <Loader2 className="w-4 h-4 animate-spin" /> Checking availability…
        </div>
      ) : error ? (
        <p className="text-sm text-red-300 py-3" data-testid="picker-error">{error}</p>
      ) : slots.length === 0 ? (
        <p className="text-sm text-[#A7A3BA] py-3" data-testid="picker-empty">
          No times available on this day. Try another date.
        </p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="picker-slots">
          {slots.map((s) => {
            const active = value === s.start;
            return (
              <button
                key={s.start}
                type="button"
                onClick={() => onChange?.(s.start)}
                className={`px-2 py-2 rounded-lg text-sm border transition-all ${
                  active
                    ? 'border-[#FF4DA6] bg-gradient-to-br from-[#FF4DA6]/20 to-[#8B5CFF]/20 text-white'
                    : 'border-white/10 bg-[#0B0A12] text-[#F4F2FF] hover:border-[#FFB6D8]/40 hover:bg-[#FFB6D8]/5'
                }`}
                data-testid={`picker-slot-${s.start}`}
              >
                {formatHumanTime(s.start)}
              </button>
            );
          })}
        </div>
      )}

      {conflictMessage && (
        <p className="text-sm text-red-300 mt-2" data-testid="picker-conflict">{conflictMessage}</p>
      )}
    </div>
  );
}

export default AvailabilitySlotPicker;
